/**
 * A tag-style input component for entering multiple string values.
 * Values are rendered as removable chips; users type and press comma/semicolon/enter to add.
 * Optionally fetches autocomplete suggestions from the companyTextFieldOptions API.
 *
 * This is the presentation + interaction component — it is UI-framework agnostic
 * and is consumed by AG-Grid cell editors and stand-alone forms.
 */
import { colorMiscBlue70, IconX } from "@rhumbix/rmbx_design_system_web"
import {
    forwardRef,
    useImperativeHandle,
    useState,
    useRef,
    useEffect,
    KeyboardEvent,
    ClipboardEvent,
    FocusEvent,
} from "react"

import { getDataFromSearchv4 } from "../../api/get"
import {
    TagEditorContainer,
    TagsContainer,
    Tag,
    TagText,
    RemoveButton,
    TagInput,
    ErrorMessage,
} from "../custom-dashboards/cell-editors/tag-editor-styles"
import { useSuggestions, SuggestionsDropdown } from "../custom-dashboards/cell-editors/useSuggestions"

const WARNING_MESSAGE_DURATION = 3000

/** Props for the StringTagInput component. */
export type tStringTagInputProps = {
    /** Current list of tag values (controlled). */
    values: string[]
    /** Called whenever the tag list changes. */
    onChange: (values: string[]) => void
    /** Optional: the model name for the companyTextFieldOptions search (e.g. "Employee"). */
    suggestionModel?: string
    /** Optional: the field name for the companyTextFieldOptions search (e.g. "department"). */
    suggestionFieldName?: string
    /** When true, auto-focus the input on mount. Defaults to true. */
    autoFocus?: boolean
    /** Optional blur handler forwarded to the underlying input after internal blur handling. */
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void
    /** Optional placeholder override. Defaults vary based on whether any tags exist. */
    placeholder?: string
}

/** Imperative handle exposed by StringTagInput. */
export type tStringTagInputHandle = {
    /**
     * Returns the committed tag values plus any non-empty pending text
     * currently in the input as a final value. Useful for callers (e.g. AG-Grid
     * cell editors) that need to retrieve a final value on commit.
     */
    getValueWithPending: () => string[]
    /** Focus the underlying input. */
    focus: () => void
}

/**
 * Reusable tag-style string input. See module doc for behavior.
 */
const StringTagInput = forwardRef<tStringTagInputHandle, tStringTagInputProps>((props, ref) => {
    const { values, onChange, suggestionModel, suggestionFieldName, autoFocus = true, onBlur, placeholder } = props

    const [inputValue, setInputValue] = useState("")
    const [error, setError] = useState("")
    const inputRef = useRef<HTMLInputElement>(null)
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const [allSuggestions, setAllSuggestions] = useState<string[]>([])

    useEffect(() => {
        if (!suggestionModel || !suggestionFieldName) {
            return
        }

        getDataFromSearchv4("", "companyTextFieldOptions", {
            model: suggestionModel,
            name: suggestionFieldName,
        })
            .then((response: Array<{ name: string }>) => {
                const names = response.map((item: { name: string }) => item.name).filter(Boolean)
                setAllSuggestions(names)
            })
            .catch(() => {
                // Silently fail — suggestions are optional
            })
    }, [suggestionModel, suggestionFieldName])

    const suggestionState = useSuggestions({
        allSuggestions,
        inputValue,
        excludeValues: values,
        onSelect: (value: string) => {
            onChange([...values, value])
            setInputValue("")
            setTimeout(() => {
                inputRef.current?.focus()
            }, 0)
        },
        showAllOnEmpty: true,
    })

    /** Show a temporary error message that auto-dismisses. */
    const showTemporaryError = (message: string): void => {
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current)
        }
        setError(message)
        errorTimeoutRef.current = setTimeout(() => {
            setError("")
            errorTimeoutRef.current = null
        }, WARNING_MESSAGE_DURATION)
    }

    useImperativeHandle(ref, () => ({
        getValueWithPending: () => {
            if (inputValue.trim()) {
                const trimmedInput = inputValue.trim()
                if (!values.includes(trimmedInput)) {
                    return [...values, trimmedInput]
                }
            }
            return values
        },
        focus: () => {
            inputRef.current?.focus()
        },
    }))

    useEffect(() => {
        if (!autoFocus) return undefined
        const timer = setTimeout(() => {
            inputRef.current?.focus()
        }, 10)

        return () => {
            clearTimeout(timer)
        }
    }, [autoFocus])

    // Clean up pending error-dismiss timer on unmount.
    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current)
            }
        }
    }, [])

    const removeValue = (index: number): void => {
        onChange(values.filter((_, i) => i !== index))
    }

    /** Add a trimmed value, surfacing a temporary error on duplicates. */
    const addValue = (value: string): void => {
        const trimmed = value.trim()
        if (!trimmed) return

        if (values.includes(trimmed)) {
            showTemporaryError(`"${trimmed}" is already in the list`)
            return
        }

        onChange([...values, trimmed])
        setInputValue("")
    }

    /** Split pasted text by comma/semicolon and add each unique value. */
    const handlePaste = (e: ClipboardEvent): void => {
        e.preventDefault()
        const pastedText = e.clipboardData.getData("text")
        const pastedValues = pastedText
            .split(/[,;]/)
            .map(v => v.trim())
            .filter(v => v !== "")

        const newValues: string[] = []
        const duplicates: string[] = []

        pastedValues.forEach(value => {
            if (values.includes(value) || newValues.includes(value)) {
                duplicates.push(value)
            } else {
                newValues.push(value)
            }
        })

        if (newValues.length > 0) {
            onChange([...values, ...newValues])
            setInputValue("")
        }

        if (duplicates.length > 0) {
            showTemporaryError(`Skipped duplicate(s): ${duplicates.join(", ")}`)
        }
    }

    /**
     * Handle keyboard events for adding/removing values.
     * Uses comma/semicolon/enter to add (not space, since values can contain spaces).
     * When suggestions are visible, delegates to suggestion navigation first.
     */
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (suggestionState.handleSuggestionKeyDown(e)) return

        if (e.key === "," || e.key === ";") {
            e.preventDefault()
            if (inputValue.trim()) {
                addValue(inputValue)
            }
        } else if (e.key === "Enter") {
            e.preventDefault()
            if (inputValue.trim()) {
                addValue(inputValue)
            }
        } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
            removeValue(values.length - 1)
        }
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
        if (suggestionState.isSuggestionBlur(e)) return
        setTimeout(() => {
            suggestionState.setShowSuggestions(false)
            if (inputValue.trim()) {
                addValue(inputValue)
            }
        }, 200)
        onBlur?.(e)
    }

    return (
        <TagEditorContainer>
            <TagsContainer>
                {values.map((value, index) => (
                    <Tag key={index}>
                        <TagText>{value}</TagText>
                        <RemoveButton
                            onClick={() => removeValue(index)}
                            type="button"
                            aria-label={`Remove ${value}`}
                        >
                            <IconX color={colorMiscBlue70} height={14} width={14} />
                        </RemoveButton>
                    </Tag>
                ))}
                <TagInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={autoFocus}
                    onChange={e => {
                        setInputValue(e.target.value)
                        suggestionState.setSelectedSuggestionIndex(-1)
                    }}
                    onKeyDown={handleKeyDown}
                    onKeyDownCapture={suggestionState.handleSuggestionKeyDownCapture}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                    onFocus={suggestionState.handleSuggestionFocus}
                    placeholder={placeholder ?? (values.length === 0 ? "Type and press Enter..." : "Add more...")}
                />
            </TagsContainer>
            <SuggestionsDropdown {...suggestionState} />
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </TagEditorContainer>
    )
})

StringTagInput.displayName = "StringTagInput"

export default StringTagInput
